// The reader's own settings, opened from their name or the User settings card:
// a Profile page (who they are, Log out, the theme, the password form) and a
// Notifications page (every notification sent to them, beside which kinds
// they get). The shell draws each page's header (SettingsGroup.jsx).
import { useEffect, useState } from "react";
import { roleNames, useMyPermissions } from "../../lib/permissions.js";
import { useCurrentUser } from "../../lib/userInfo.js";
import { signedInRole } from "../../lib/userRoles.js";
import {
    NOTIFICATION_TYPES, getUserNotificationPreferences, notificationType, updateUserNotificationPreferences,
} from "../../lib/userNotificationClient.js";
import { UserNotificationHistory } from "../UserNotificationInbox.jsx";
import Button from "../ui/Button.jsx";
import Checkbox from "../ui/Checkbox.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import Panel from "../ui/Panel.jsx";
import SettingRow from "../ui/SettingRow.jsx";
import { SkeletonBar } from "../ui/Skeleton.jsx";
import Switch from "../ui/Switch.jsx";
import PasswordSection from "./PasswordSection.jsx";

// Who is signed in, where and as what, with the way out beside it.
function SignedIn({ onLogout }) {
    const user = useCurrentUser();
    const caps = useMyPermissions();
    const role = signedInRole(roleNames(caps), user?.currentTenant?.role);
    const tenant = user?.currentTenant?.tenantName;
    const where = role && tenant ? `${role} in ${tenant}` : role || tenant || null;
    return (
        <SettingRow
            title="Signed in as"
            description={(
                <>
                    <span className="block text-[color:var(--ink)]" data-account-email="">{user?.email || "Unknown account"}</span>
                    {where ? <span className="block">{where}</span> : null}
                </>
            )}
            data-account-signed-in=""
        >
            {onLogout ? <Button onClick={onLogout}>Log out</Button> : null}
        </SettingRow>
    );
}

export function AccountProfile({ theme = "dark", onToggleTheme = null, onLogout = null }) {
    return (
        <div className="grid w-full max-w-[720px] content-start" data-account-profile="">
            <SignedIn onLogout={onLogout} />
            <SettingRow title="Dark theme" description="Use the dark theme on this browser.">
                <Switch checked={theme === "dark"} onChange={() => onToggleTheme?.()} disabled={!onToggleTheme} label="Dark theme" />
            </SettingRow>
            <div className="pt-[24px] empty:hidden">
                <PasswordSection />
            </div>
        </div>
    );
}

// The server answers with every type, each with whether it is on and whether
// it applies to this reader at all.
function readPreferences(raw) {
    const out = {};
    for (const p of raw?.preferences || []) {
        const kind = notificationType(p.kind)?.kind;
        if (kind) out[kind] = { enabled: !!p.enabled, applicable: !!p.applicable };
    }
    return out;
}

function NotificationSettings() {
    const [prefs, setPrefs] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        const abort = new AbortController();
        getUserNotificationPreferences({ signal: abort.signal })
            .then((raw) => setPrefs(readPreferences(raw)))
            .catch((err) => { if (!abort.signal.aborted) setError(err?.message || String(err)); });
        return () => abort.abort();
    }, []);

    async function change(kind, enabled) {
        const before = prefs;
        setPrefs({ ...prefs, [kind]: { ...prefs[kind], enabled } });
        setSaving(kind);
        setError(null);
        try {
            setPrefs(readPreferences(await updateUserNotificationPreferences(kind, enabled)));
        } catch (err) {
            setPrefs(before);
            setError(err?.message || String(err));
        } finally {
            setSaving(null);
        }
    }

    // While loading, every known type stands in with a placeholder for its
    // box, so the list does not grow from one line when the answer lands.
    const loading = !prefs && !error;
    const types = prefs ? NOTIFICATION_TYPES.filter((t) => prefs[t.kind]?.applicable) : loading ? NOTIFICATION_TYPES : [];
    const groups = [...new Set(types.map((t) => t.group))];
    return (
        <Panel as="section" title="What you're notified about" aria-label="Notification settings" aria-busy={loading} data-notification-settings="" className="gap-[6px]">
            {loading ? <span className="sr-only">Loading your notification settings…</span> : null}
            {prefs && !types.length ? <p className="m-0 text-[12.5px] text-[color:var(--muted)]">No notifications apply to your role.</p> : null}
            {groups.map((group) => (
                <fieldset key={group} className="m-0 grid min-w-0 border-0 p-0">
                    <legend className="p-0 pb-[2px] pt-[8px] text-[12px] text-[color:var(--muted)]">{group}</legend>
                    {types.filter((t) => t.group === group).map((t) => loading ? (
                        <label key={t.kind} className="flex items-center justify-between gap-[12px] border-b border-[color:var(--line-soft)] py-[8px] text-[13px] leading-[1.35] text-[color:var(--muted)] last:border-b-0">
                            {t.label}
                            <SkeletonBar className="w-[16px] shrink-0" />
                        </label>
                    ) : (
                        <Checkbox
                            key={t.kind}
                            checked={prefs[t.kind].enabled}
                            disabled={saving != null}
                            onChange={(checked) => change(t.kind, checked)}
                            label={t.label}
                            className="w-full flex-row-reverse justify-between border-b border-[color:var(--line-soft)] py-[8px] leading-[1.35] last:border-b-0"
                        />
                    ))}
                </fieldset>
            ))}
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

export function AccountNotifications() {
    return (
        <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_300px] items-start gap-[28px] max-[999px]:grid-cols-1" data-account-notifications="">
            <UserNotificationHistory />
            <NotificationSettings />
        </div>
    );
}
